import React, { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import TextInput from "../inputs/TextInput";
import Select from "./Select";

const AddressFinder = ({
	register,
	error,
	placeholder = "Start typing your address",
	required = false,
	className = "",
	id,
	setValue,
	watch,
	errors,
	config,
	countryDropdown,
}) => {
	const [searchValue, setSearchValue] = useState("");
	const [isAddressSelected, setIsAddressSelected] = useState(false);
	const [isManualEntry, setIsManualEntry] = useState(false);
	const [autocomplete, setAutocomplete] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const searchInputRef = useRef(null);
	const autocompleteRef = useRef(null); // Add this ref

	// UK county options passed via PHP config (if available)
	const ukCountyOptions =
		config?.uk_counties && Array.isArray(config.uk_counties)
			? [{ label: "Please select...", value: "" }, ...config.uk_counties]
			: null;

	// Get the Google Maps API key from the existing configuration
	const GOOGLE_MAPS_API_KEY = "AIzaSyA6U6RdhSvZp19IHrnZZ0d8io4ri70iTow";

	// Check for existing form values on mount and restore state
	useEffect(() => {
		console.log("searchValue", searchValue);
		console.log("isAddressSelected", isAddressSelected);
		console.log("isManualEntry", isManualEntry);
		console.log("autocomplete", autocomplete);
		console.log("isLoading", isLoading);
		console.log("watch", watch);
		console.log("searchInputRef", searchInputRef);
		console.log("autocompleteRef", autocompleteRef);
		if (watch && !searchValue) {
			const mainAddressValue = watch(id);
			const addressLine1 = watch(`${id}_address_line_1`);
			const town = watch(`${id}_town`);
			const county = watch(`${id}_county`);
			const postcode = watch(`${id}_postcode`);
			// If we have address details in the form, restore the selected state
			if (addressLine1 || town || county || postcode) {
				setIsAddressSelected(true);
				setSearchValue(mainAddressValue || "");
			}
		}
	}, [watch(id), id]);

	// Load Google Places API and initialize autocomplete
	useEffect(() => {
		if (!window.google || !window.google.maps) {
			const script = document.createElement("script");
			script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
			script.async = true;
			script.defer = true;
			script.onload = initializeAutocomplete;
			script.onerror = () => {
				console.error("Failed to load Google Places API");
			};
			document.head.appendChild(script);
		} else {
			initializeAutocomplete();
		}

		return () => {
			if (autocompleteRef.current) {
				window.google.maps.event.clearInstanceListeners(
					autocompleteRef.current
				);
			}
		};
	}, []); // Only run once on mount

	// Re-initialize autocomplete when switching back to search mode
	useEffect(() => {
		if (
			!isAddressSelected &&
			!isManualEntry &&
			window.google &&
			window.google.maps &&
			searchInputRef.current
		) {
			// Small delay to ensure the input is rendered
			setTimeout(() => {
				initializeAutocomplete();
			}, 100);
		}
	}, [isAddressSelected, isManualEntry]); // Re-run when isAddressSelected or isManualEntry changes

	const initializeAutocomplete = () => {
		if (!window.google || !window.google.maps || !searchInputRef.current) {
			return;
		}

		// Clean up any existing autocomplete instance
		if (autocompleteRef.current) {
			window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
		}

		const autocompleteInstance = new window.google.maps.places.Autocomplete(
			searchInputRef.current,
			{
				types: ["geocode"],
				componentRestrictions: { country: "gb" }, // Restrict to UK addresses
				fields: [
					"address_components",
					"formatted_address",
					"geometry",
					"place_id",
				],
			}
		);
		autocompleteInstance.setOptions({
			strictBounds: true, // More restrictive matching
		});

		// Store in ref for reliable access
		autocompleteRef.current = autocompleteInstance;

		// Use the instance directly in the event listener instead of relying on state
		const handlePlaceSelectClosure = () => {
			// eslint-disable-next-line no-console
			console.log("[AddressFinder] google place_changed fired");
			const place = autocompleteRef.current.getPlace();
			handlePlaceSelect(place, autocompleteRef.current);
		};

		//listen for place_changed and change events
		autocompleteInstance.addListener("place_changed", handlePlaceSelectClosure);

		setAutocomplete(autocompleteInstance);
	};

	const handlePlaceSelect = (place, autocompleteInstance) => {
		console.log("handlePlaceSelect");
		console.log("[AddressFinder] handlePlaceSelect fired");
		console.log("[AddressFinder] place", place);
		console.log("[AddressFinder] autocompleteInstance", autocompleteInstance);
		if (!place || !place.place_id) {
			return;
		}

		setIsLoading(true);

		// Parse address components
		const addressComponents = place.address_components || [];
		const newAddressFields = {
			address_line_1: "",
			address_line_2: "",
			town: "",
			county: "",
			postcode: "",
			country: "United Kingdom",
		};

		// Extract address components
		addressComponents.forEach((component) => {
			const types = component.types;

			if (types.includes("street_number")) {
				newAddressFields.address_line_1 = component.long_name;
			} else if (types.includes("route")) {
				newAddressFields.address_line_1 +=
					(newAddressFields.address_line_1 ? " " : "") + component.long_name;
			} else if (types.includes("subpremise")) {
				newAddressFields.address_line_2 = component.long_name;
			} else if (types.includes("locality") || types.includes("postal_town")) {
				newAddressFields.town = component.long_name;
			} else if (types.includes("administrative_area_level_2")) {
				newAddressFields.county = component.long_name;
			} else if (types.includes("postal_code")) {
				newAddressFields.postcode = component.long_name;
			} else if (types.includes("country")) {
				newAddressFields.country = component.long_name;
			}
		});

		// If we don't have a street number, try to extract from formatted address
		if (!newAddressFields.address_line_1 && place.formatted_address) {
			const lines = place.formatted_address.split(",");
			if (lines.length > 0) {
				newAddressFields.address_line_1 = lines[0].trim();
			}
		}

		setIsAddressSelected(true);
		setIsLoading(false);

		// Update form values if setValue is available
		if (setValue) {
			Object.keys(newAddressFields).forEach((field) => {
				const fieldId = `${id}_${field}`;
				setValue(fieldId, newAddressFields[field], { shouldValidate: true });
			});

			// Set the main address field to the formatted address
			setValue(id, place.formatted_address, { shouldValidate: true });
		}
	};

	const handleSearchChange = (e) => {
		console.log("handleSearchChange");
		const value = e.target.value;
		console.log("[AddressFinder] handleSearchChange fired");
		console.log("[AddressFinder] value", value);
		setSearchValue(value);

		// Update the form value for validation
		if (setValue) {
			console.log("setValue", value);
			setValue(id, value, { shouldValidate: true });
		}

		if (!value) {
			setIsAddressSelected(false);
			// Clear form values when search is cleared
			if (setValue) {
				const fields = [
					"address_line_1",
					"address_line_2",
					"town",
					"county",
					"postcode",
					"country",
				];
				fields.forEach((field) => {
					const fieldId = `${id}_${field}`;
					setValue(fieldId, "", { shouldValidate: true });
				});
			}
		}
	};

	const handleManualEntry = () => {
		setIsManualEntry(true);
		setSearchValue("");
		// Clear the main address field to allow manual entry
		if (setValue) {
			setValue(id, "", { shouldValidate: true });
		}
	};

	//add event lsitenr that console logs on place_changed and change events
	useEffect(() => {
		if (autocompleteRef.current) {
			autocompleteRef.current.addListener("place_changed", function () {
				console.log("place_changed event fired");
			});
			autocompleteRef.current.addListener("change", function () {
				console.log("change event fired");
			});
		}
	}, [autocompleteRef.current]);

	//set country to United Kingdom if it is not set
	useEffect(() => {
		if (!watch(`${id}_country`) && setValue) {
			setValue(`${id}_country`, "United Kingdom", { shouldValidate: true });
		}
	}, [watch(`${id}_country`), id, setValue]);

	// Track selected country
	const selectedCountry = watch ? watch(`${id}_country`) : undefined;
	// If country is changed away from UK, clear county value
	useEffect(() => {
		if (selectedCountry && selectedCountry !== "United Kingdom" && setValue) {
			setValue(`${id}_county`, "", { shouldValidate: true });
		}
	}, [selectedCountry, id, setValue]);

	return (
		<div
			className={classNames(
				"address-finder",
				{
					"address-finder--selected": isAddressSelected || isManualEntry,
				},
				className
			)}
		>
			{/* Search input - always visible but conditionally styled */}
			<div
				className={classNames("address-finder__search", {
					"d-none": isAddressSelected || isManualEntry,
				})}
			>
				<TextInput
					ref={searchInputRef}
					label=""
					value={searchValue}
					onChange={handleSearchChange}
					placeholder={placeholder}
					error={error}
					descriptionBelow="Start typing your address and select from the suggestions"
					hideLabel={true}
				/>
				{isLoading && (
					<div className="address-finder__loading">
						Loading address details...
					</div>
				)}
				<div className="address-finder__manual-entry">
					<button
						type="button"
						className="address-finder__manual-btn"
						onClick={handleManualEntry}
					>
						Add Address Manually
					</button>
				</div>
			</div>

			{/* Address fields - always rendered but conditionally visible */}
			<div
				className={classNames("address-finder__fields", {
					"d-none": !isAddressSelected && !isManualEntry,
				})}
			>
				<TextInput
					label="Address Line 1"
					{...register(`${id}_address_line_1`, {
						required: "Address Line 1 is required",
					})}
					placeholder="House number and street"
					required={true}
					error={errors?.[`${id}_address_line_1`]}
				/>

				<TextInput
					label="Address Line 2"
					{...register(`${id}_address_line_2`)}
					placeholder="Apartment, suite, etc. (optional)"
					required={false}
				/>

				<TextInput
					label="Town/City"
					{...register(`${id}_town`, {
						required: "Town/City is required",
					})}
					placeholder="Town or city"
					required={true}
					error={errors?.[`${id}_town`]}
				/>

				{/* County - show only when country is United Kingdom with updated json */}
				{(!selectedCountry ||
					selectedCountry === "" ||
					selectedCountry === "United Kingdom") &&
					(ukCountyOptions ? (
						<Select
							label="County"
							id={`${id}_county`}
							register={register}
							required={true}
							hideLabel={false}
							options={ukCountyOptions}
							selectBlankOption={false}
							error={errors?.[`${id}_county`]}
							className=""
						/>
					) : (
						<TextInput
							label="County"
							{...register(`${id}_county`, {
								required: "County is required",
							})}
							placeholder="County"
							required={true}
							error={errors?.[`${id}_county`]}
						/>
					))}
				


				<TextInput
					label="Postcode"
					{...register(`${id}_postcode`, {
						required: "Postcode is required",
						pattern: {
							value:
								/^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$/,
							message: "Please enter a valid UK postcode",
						},
					})}
					placeholder="Postcode"
					required={true}
					error={errors?.[`${id}_postcode`]}
				/>

					
				{countryDropdown && (
					config?.countries && Array.isArray(config.countries) ? (
					<Select
						label="Country"
						id={`${id}_country`}
						register={register}
						required={true}
						hideLabel={false}
						options={[
							{ label: "Please select...", value: "" },
							...config.countries,
						]}
						selectBlankOption={false}
						error={errors?.[`${id}_country`]}
						className=""
					/>
				) : (
					<TextInput
						label="Country"
						{...register(`${id}_country`, {
							required: "Country is required",
						})}
						placeholder="Country"
						required={true}
						error={errors?.[`${id}_country`]}
					/>
				))}
			</div>

			{/* Hidden input for the main address field */}
			{/* <input type="hidden" {...register(id)} /> */}

			{/* Show main address field error if it exists */}
			{error && <div className="field__error">{error.message || error}</div>}
		</div>
	);
};

export default AddressFinder;
